import ALL_SETS_ROUTES from '../screens/DataManager/AllSets/routes';
import SPECIFIC_SET_ROUTES from '../screens/DataManager/SpecificSet/routes';
import getPortalSlug from './getPortalSlug';
const home = '/';
const logIn = '/logIn';
const resetPassword = '/reset-password';
const welcome = '/welcome';
const acceptInvite = '/invitations/accept';
const expiredInvitation = '/expiredInvitation';
const alreadyAcceptedInvitation = '/alreadyAcceptedInvitation';
const forgotPassword = '/forgot-password';
const wallboardSetup = '/wallboard-setup';
const signOut = '/signOut';
const myAccount = '/my-account';

const reports = '/reports';
const showReport = '/reports/:reportId';
// Chrome-less report render used by the headless scheduled-report renderer.
const printReport = '/print/reports/:reportId';
export const buildPrintReport = ({ id }: { id: string }) =>
  `/print/reports/${id}`;
// Chrome-less dashboard render used by the headless scheduled-delivery renderer.
const printDashboard = '/print/dashboards/:dashboardId';
export const buildPrintDashboard = ({ id }: { id: string }) =>
  `/print/dashboards/${id}`;
// Chrome-less render of a bonus program's rules markdown, opened in a new tab.
const bonusRules = '/bonus-rules/:bonusProgramId';
export const buildBonusRules = ({
  bonusProgramId,
}: {
  bonusProgramId: string;
}) => `/bonus-rules/${bonusProgramId}`;
export const buildReportShow = ({
  id,
  isEditing,
  portal,
}: {
  id: string;
  isEditing?: boolean;
  portal: Portal | undefined;
}) =>
  isEditing
    ? `/${getPortalSlug(portal)}reports/${id}?editing=true`
    : `/${getPortalSlug(portal)}reports/${id}`;

const dashboards = '/dashboards';
const showDashboard = '/dashboards/:dashboardId';

export const buildDashboardShow = ({
  id,
  isEditing,
  portal,
  gadgetId,
}: {
  id: string;
  isEditing?: boolean;
  portal: Portal | undefined;
  gadgetId?: string;
}) => {
  const search = new URLSearchParams();

  if (isEditing) {
    search.append('editing', 'true');
  }
  if (gadgetId) {
    search.append('gadgetId', gadgetId);
  }

  const base = `/${getPortalSlug(portal)}dashboards/${id}`;
  if (search.size > 0) {
    return `${base}?${search}`;
  }
  return base;
};

const shared = '/shared';

// Agent Management — the Insights agent's knowledge base, versions and canary
// pins (staff only).
const insightsAgent = '/insights/agent';
export const buildInsightsAgent = (portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}insights/agent`;

// Insights Chat (full-page top-level AI chat)
const insightsChat = '/insights';
export const buildInsightsChat = (portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}insights`;

// Settings
const settings = '/settings';
export const buildSettings = (portal: Portal | undefined) => {
  return `/${getPortalSlug(portal)}settings`;
};
const allUsersSettings = '/settings/users';
export const buildAllUsersSettings = (portal: Portal | undefined) => {
  return `/${getPortalSlug(portal)}settings/users`;
};
export const buildShowUser = (id: string, portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}settings/users/${id}`;
const showUserSettings = '/settings/users/:userId';
const billingSettings = '/settings/billing';
export const buildBillingSettings = (portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}settings/billing`;
const fiscalPeriodSettings = '/settings/fiscal-periods';
export const buildFiscalPeriodSettings = (portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}settings/fiscal-periods`;
const integrationsSettings = '/settings/connectors';
export const buildIntegrationsSettings = (portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}settings/connectors`;
const providerSettings = '/settings/connectors/:provider';
export const buildProviderShow = (
  provider: string,
  portal: Portal | undefined,
) => `/${getPortalSlug(portal)}settings/connectors/${provider}`;
const irGuide = '/settings/connectors/ir-guide';
const motiveOAuthRedirect = '/settings/connectors/motive/oauth_redirect';
export const buildIrGuide = (portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}settings/connectors/ir-guide`;
const ssoConfigurationsSettings = '/settings/sso';
export const buildSsoConfigurationsSettings = (portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}settings/sso`;
const paceSettings = '/settings/pace';
export const buildPaceSettings = (portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}settings/pace`;
const microsoftSsoRedirect = '/settings/sso/microsoft_consent_redirect';

const advancedSettings = '/settings/advanced';
export const buildAdvancedSettings = (portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}settings/advanced`;
const advancedSettingsUi = '/settings/advanced/ui';
export const buildAdvancedSettingsUi = (portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}settings/advanced/ui`;
const advancedSettingsData = '/settings/advanced/data';
export const buildAdvancedSettingsData = (portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}settings/advanced/data`;
const advancedSettingsDriverBonus = '/settings/advanced/driver-bonus';
export const buildAdvancedSettingsDriverBonus = (portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}settings/advanced/driver-bonus`;
const commissions = '/settings/commissions';
const copyContent = '/settings/copy-content';
export const buildCopyContent = (portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}settings/copy-content`;
const copyContentHistory = '/settings/copy-content-history';
export const buildCopyHistory = (portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}settings/copy-content-history`;
const localesSettings = '/settings/locale';

// Settings - slas
const allTargetCategories = '/settings/slas';
export const buildAllTargetCategories = (portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}settings/slas`;
const targetCategoryShow = '/settings/slas/:category';
export const buildTargetCategoryShow = (
  category: string,
  portal: Portal | undefined,
) => `/${getPortalSlug(portal)}settings/slas/${category}`;
const targetShow = '/settings/slas/:category/:target';
export const buildTargetShow = (
  category: string,
  target: string,
  portal: Portal | undefined,
) => `/${getPortalSlug(portal)}settings/slas/${category}/${target}`;

// Goals
const goals = '/goals';
const showGlobalGoal = '/goals/:goalId';
export const buildShowGlobalGoal = (goalId: string) => `/goals/${goalId}`;
const kpiLists = '/kpi-lists';
const showGlobalKpiList = '/kpi-lists/:scorecardId';
export const buildShowGlobalKpiList = ({
  scorecardId,
  kpiId,
  periodEnd,
  periodStart,
  portal,
}: {
  scorecardId: string;
  kpiId?: string;
  periodStart?: string;
  periodEnd?: string;
  portal?: Portal | undefined;
}) => {
  if (kpiId && periodStart && periodEnd) {
    return `/${getPortalSlug(portal)}kpi-lists/${scorecardId}?kpiId=${kpiId}&periodStart=${periodStart}&periodEnd=${periodEnd}`;
  }

  return `/${getPortalSlug(portal)}kpi-lists/${scorecardId}`;
};
const showCampaignGoal = '/workspaces/:workspaceId/goals/:goalTabId/:goalId';
export const buildShowCampaignGoal = (
  goalId: string,
  campaignId: string,
  goalsTabId: string,
) => `/workspaces/${campaignId}/goals/${goalsTabId}/${goalId}`;
const showCampaignKpiList =
  '/workspaces/:workspaceId/kpi-lists/:scorecardTabId/:scorecardId';

const showOrderDetails = '/details/:orderNumber';
export const buildShowOrderDetails = (id: string, portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}details/${id}`;

const boards = '/boards';
const showBoard = '/boards/:boardId';
export const buildShowBoard = (id: string) => `/boards/${id}`;
export const buildCustomerLaneSlideOutSearchQuery = ({
  lane,
  customer,
  customerType,
  documentId,
}: {
  lane: string;
  customer: string;
  customerType: string;
  documentId: string;
}) => {
  if (customerType === 'customer') {
    return `lane=${lane}&customerType=${customerType}&customer=${customer}&documentId=${documentId}`;
  } else {
    return `lane=${lane}&customerType=${customerType}&billTo=${customer}&documentId=${documentId}`;
  }
};
export const buildCustomerLaneSlideOutSearch = ({
  boardId,
  lane,
  customer,
  customerType,
  documentId,
}: {
  boardId: string;
  lane: string;
  customer: string;
  customerType: string;
  documentId: string;
}) =>
  `${buildShowBoard(boardId)}?${buildCustomerLaneSlideOutSearchQuery({
    lane,
    customer,
    customerType,
    documentId,
  })}`;

// Studio's URLs sit under a V2 portal's slug (none in V1 and V3), so the
// portal survives a reload and Save returns to it
const studio = '/studio';
// A dashboard or report opened in Studio
const studioEdit = '/studio/:contentType/:contentId';
export const buildStudioEdit = ({
  contentType,
  id,
  portal,
}: {
  contentType: Studio.ContentType;
  id: string;
  portal: Portal | undefined;
}) => `/${getPortalSlug(portal)}studio/${contentType}/${id}`;
// Studio on a new, blank dashboard or report
export const buildStudioNew = ({
  contentType,
  portal,
}: {
  contentType: Studio.ContentType;
  portal: Portal | undefined;
}) => `/${getPortalSlug(portal)}studio?new=${contentType}`;
// Studio on a copy of a dashboard template: saved as a new dashboard
export const buildStudioFromTemplate = ({
  id,
  portal,
}: {
  id: string;
  portal: Portal | undefined;
}) => `/${getPortalSlug(portal)}studio/dashboard/${id}?copy=1`;
// Where content is listed: V3's All Content, else the portal's (or V1's)
// dashboards. V2 has no dashboards list outside a portal.
export const buildContentIndex = ({
  isV3,
  isPortalsEnabled,
  portal,
}: {
  isV3: boolean;
  isPortalsEnabled: boolean;
  portal: Portal | undefined;
}) => {
  if (isV3) {
    return '/content';
  }
  if (isPortalsEnabled && !portal) {
    return home;
  }
  return `/${getPortalSlug(portal)}dashboards`;
};

const wallBoardConfigs = '/wallboards';
const showWallBoardConfig = `/wallboards/:wallBoardId/edit`;
export const buildShowWallBoardConfig = (
  id: string,
  portal: Portal | undefined,
) => `/${getPortalSlug(portal)}wallboards/${id}/edit`;

const showWallBoard = `/wallboards/:wallBoardId`;
export const buildShowWallBoard = (id: string, portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}wallboards/${id}`;

// Workspaces
const workspaces = '/workspaces';
const showWorkSpace = `/workspaces/:workSpaceId`;
const showWorkSpaceTab = `/workspaces/:workSpaceId/:tabType/:tabId`;
export const buildShowWorkSpace = (id: string) => `/workspaces/${id}`;
export const buildShowWorkSpaceTab = ({
  workSpaceId,
  tabType,
  tabId,
}: {
  workSpaceId: string;
  tabType: string;
  tabId?: string;
}) => `/workspaces/${workSpaceId}/${tabType}/${tabId}`;

// Performance Apps
const performanceApps = '/performance-apps';
const showPerformanceApp = `/performance-apps/:workSpaceId`;
const showPerformanceAppTab = `/performance-apps/:workSpaceId/:tabType/:tabId`;
export const buildShowPerformanceApp = (
  id: string,
  portal: Portal | undefined,
) => `/${getPortalSlug(portal)}performance-apps/${id}`;
export const buildShowPerformanceAppTab = ({
  workSpaceId,
  tabType,
  tabId,
  portal,
}: {
  workSpaceId: string;
  tabType: string;
  tabId: string;
  portal?: Portal | undefined;
}) =>
  `/${getPortalSlug(portal)}performance-apps/${workSpaceId}/${tabType}/${tabId}`;

// Target Apps
const showTargetApp = `/target-apps/:targetAppId`;
const showTargetAppTasksTab = `/target-apps/:targetAppId/tasks`;
const showTargetAppContentTab = `/target-apps/:targetAppId/:tabType/:tabId`;
export const buildShowTargetApp = (id: string, portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}target-apps/${id}`;
const buildShowTargetAppTasksTab = (id: string, portal: Portal | undefined) =>
  `/${getPortalSlug(portal)}target-apps/${id}/tasks`;
export const buildShowTargetAppContentTab = ({
  targetAppId,
  tabType,
  tabId,
  portal,
}: {
  targetAppId: string;
  tabType: string;
  tabId?: string;
  portal: Portal | undefined;
}) => {
  if (tabType === 'tasks') {
    return buildShowTargetAppTasksTab(targetAppId, portal);
  }
  return `/${getPortalSlug(portal)}target-apps/${targetAppId}/${tabType}/${tabId}`;
};

// Entity Details
const entityAppRedirect = `/entities/:entityAppSlug`;
export const buildEntityAppRedirect = ({
  entityApp,
  portal,
}: {
  entityApp: EntityDetails.App;
  portal?: Portal | undefined;
}) => {
  return `/${getPortalSlug(portal)}entities/${entityApp.urlSlug}`;
};
/**
 * Be careful changing this and showEntityAppContentTab
 * This is tightly coupled in {@link useBuildEntityLink} without typescript support
 */
const showEntityApp = `/entities/:entityAppSlug/:value`;
const showEntityAppContentTab = `/entities/:entityAppSlug/:value/:tabType/:tabId`;
export const buildShowEntityApp = ({
  entityApp,
  value,
  portal,
}: {
  entityApp: EntityDetails.App;
  value?: string;
  portal?: Portal;
}) => {
  if (!value || value === '') {
    return `/${getPortalSlug(portal)}entities/${entityApp.urlSlug}`;
  }

  return `/${getPortalSlug(portal)}entities/${entityApp.urlSlug}/${encodeURIComponent(value)}`;
};
export const buildShowEntityAppContentTab = ({
  entityApp,
  value,
  tabType,
  tabId,
  portal,
}: {
  entityApp: EntityDetails.App;
  value?: string;
  tabType: string;
  tabId: string;
  portal?: Portal;
}) => {
  if (!value || value === '') {
    return `/${getPortalSlug(portal)}entities/${entityApp.urlSlug}/${tabType}/${tabId}`;
  }

  return encodeURI(
    `/${getPortalSlug(portal)}entities/${entityApp.urlSlug}/${encodeURIComponent(value)}/${tabType}/${tabId}`,
  );
};

const showEntityAppUnfiltered = `/entities/:entityAppSlug`;
const showEntityAppContentTabUnfiltered = `/entities/:entityAppSlug/:tabType/:tabId`;

const driverBonus = '/driverBonus';

const datasetDefinitions = {
  home: ALL_SETS_ROUTES.BASE,
  dataset: SPECIFIC_SET_ROUTES.BASE,
  field: `${SPECIFIC_SET_ROUTES.BASE}?field=:field`,
  buildShowDataset: SPECIFIC_SET_ROUTES.buildFieldSettings,
  buildShowField: (type: string, field: string, portal: Portal | undefined) =>
    `${SPECIFIC_SET_ROUTES.buildFieldSettings(type, portal)}?field=${field}`,
};

// On-boarding
const onBoardingLanding = '/';
const onBoardingBilling = '/billing';
const onBoardingIrGuide = '/ir-guide';
const onBoardingProviderSettings = '/connectors/:provider';

export const buildOnboardingProviderShow = (provider: string) =>
  `/connectors/${provider}`;

const engagementPortalManagersAccess = '/access/:type';
const engagementPortalMappedAccess = '/access/:type/:entityId/:entityAppId';

/**
 * There are our routes for the legacy UI, the admin portal, onboarding and unauthenticated
 * When we stop supporting the legacy UI, lets consider breaking this file up into
 * a number of files, one for each area of concern.
 *
 * Additionally, the admin portal routes ought to be prefixed with /admin
 *
 * I opted not to do it during the initial development to save time
 */
const AppRoutes = {
  home,
  logIn,
  welcome,
  acceptInvite,
  forgotPassword,
  expiredInvitation,
  alreadyAcceptedInvitation,
  resetPassword,
  wallboardSetup,
  onBoarding: {
    landing: onBoardingLanding,
    billing: onBoardingBilling,
    provider: onBoardingProviderSettings,
    irGuide: onBoardingIrGuide,
  },
  loggedIn: {
    reports,
    showReport,
    printReport,
    printDashboard,
    bonusRules,
    dashboards,
    showDashboard,
    signOut,
    myAccount,
    allUsersSettings,
    showUserSettings,
    localesSettings,
    allTargetCategories,
    targetCategoryShow,
    targetShow,
    settings,
    billingSettings,
    fiscalPeriodSettings,
    integrationsSettings,
    irGuide,
    motiveOAuthRedirect,
    providerSettings,
    showOrderDetails,
    boards,
    showBoard,
    studio,
    studioEdit,
    wallBoardConfigs,
    showWallBoardConfig,
    showWallBoard,
    workspaces,
    shared,
    insightsChat,
    insightsAgent,
    showWorkSpace,
    showWorkSpaceTab,
    performanceApps,
    showPerformanceApp,
    showPerformanceAppTab,
    showTargetApp,
    showTargetAppTasksTab,
    showTargetAppContentTab,
    entityAppRedirect,
    showEntityApp,
    showEntityAppContentTab,
    showEntityAppUnfiltered,
    showEntityAppContentTabUnfiltered,
    driverBonus,
    goals,
    showCampaignGoal,
    showCampaignKpiList,
    showGlobalGoal,
    datasetDefinitions,
    advancedSettings,
    advancedSettingsUi,
    advancedSettingsData,
    advancedSettingsDriverBonus,
    commissions,
    kpiLists,
    showGlobalKpiList,
    ssoConfigurationsSettings,
    microsoftSsoRedirect,
    paceSettings,
    copyContent,
    copyContentHistory,
    engagementPortalManagersAccess,
    engagementPortalMappedAccess,
  },
};

export default AppRoutes;
